import React, { useEffect, useState, useRef } from 'react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Check } from 'lucide-react';

interface BorderedMultiSelectProps {
  disabled?: boolean;
  options: Array<{ value: string; label: string }>;
  value?: string[];
  onChange?: (values: string[]) => void;
  onOpenChange?: (open: boolean) => void;
  label?: string;
  error?: string;
  className?: string;
  textClassName?: string;
  searchEnabled?: boolean;
  placeholder?: string;
}

const BorderedMultiSelect: React.FC<BorderedMultiSelectProps> = ({
  disabled = false,
  options,
  value = [],
  onChange,
  onOpenChange,
  label,
  error,
  className,
  textClassName,
  searchEnabled = false,
  placeholder = 'Select options',
}) => {
  const [open, setOpen] = useState(false);
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedValues, setSelectedValues] = useState<string[]>(value);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync with external value prop
  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  useEffect(() => {
    if (searchQuery) {
      setFilteredOptions(options.filter((opt) => opt.label && opt.label.toLowerCase().includes(searchQuery.toLowerCase())));
    } else {
      setFilteredOptions(options);
    }
  }, [searchQuery, options]);

  // Keep input focused when dropdown is open and search is enabled
  useEffect(() => {
    if (open && searchEnabled && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [open, searchEnabled]);

  // Maintain focus when filtered options change (prevents focus loss during typing)
  useEffect(() => {
    if (open && searchEnabled && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      requestAnimationFrame(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }
  }, [filteredOptions, open, searchEnabled]);

  const handleToggleOption = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  const handleSelectAll = () => {
    const allValues = filteredOptions.map(opt => opt.value);
    const newValues = allValues.every(val => selectedValues.includes(val))
      ? selectedValues.filter(val => !allValues.includes(val))
      : [...new Set([...selectedValues, ...allValues])];

    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  };

  return (
    <div className="flex flex-col">
      <div
        className={`group relative rounded cursor-pointer ${error ? 'border-red-500' : ''} ${className}`}
        style={{
          paddingLeft: '1px',
          paddingRight: '5px',
          display: 'inline-block',
          maxWidth: '250px',
          maxHeight: 36,
        }}
      >
        <DropdownMenu
          open={open}
          onOpenChange={handleOpenChange}
        >
          {label && (
            <label className="absolute -top-2 left-3 mb-1 text-xs font-light text-gray-500 z-10 pointer-events-none">
              {label}
            </label>
          )}
          <DropdownMenuTrigger asChild>
            <Button
              disabled={disabled}
              className={`appearance-none bg-transparent w-full px-3 py-2 rounded transition-all duration-300
                border-1 border-transparent 
                group-hover:border-gray-500 
                group-hover:border 
                ${disabled ? '!cursor-not-allowed' : 'cursor-pointer'}
                ${error ? 'group-hover:border-red-500 group-focus-within:border-red-500' : ''}
              `}
              variant="ghost"
              size="med"
              onClick={() => handleOpenChange(!open)}
            >
              <span className={`${textClassName || 'text-gray-500'} ${disabled ? '!cursor-not-allowed' : 'cursor-pointer'} truncate`}>
                {getDisplayText()}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            onCloseAutoFocus={(e) => {
              if (searchEnabled) {
                e.preventDefault();
              }
            }}
            className="min-w-[200px]"
          >
            {searchEnabled && (
              <div className="px-1">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search"
                  className="w-full px-3 py-2 border-b border-gray-400 text-sm text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    e.target.focus();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && searchQuery) {
                      e.stopPropagation();
                      setSearchQuery('');
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
            )}
            {filteredOptions.length > 0 && (
              <div className="px-1 py-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  className="w-full text-left px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-sm"
                >
                  {filteredOptions.every(opt => selectedValues.includes(opt.value)) ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-gray-500 text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedValues.includes(opt.value);
                return (
                  <DropdownMenuItem
                    key={opt.value}
                    onSelect={(e) => {
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(opt.value);
                    }}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div className={`flex items-center justify-center w-4 h-4 border rounded ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="flex-1">
                        {opt.label}
                      </span>
                    </div>
                  </DropdownMenuItem>
                );
              })
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {error && (
        <span className="mt-1 text-xs text-red-500">
          {error}
        </span>
      )}
    </div>
  );
};

export default BorderedMultiSelect;
